import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { FontVerticalMetricsPt } from './types/font-vertical-metrics-pt'
import type { GlyphInkMetricsPt } from './types/glyph-ink-metrics-pt'

/**
 * Unit convention for everything in the document canvas: **one document unit is one point, and one
 * point is one CSS px**. Page geometry already works this way — `book-size-dimensions.ts` returns
 * points (`12 * PT_PER_INCH // 864`) and `center-canvas/center-canvas.tsx` renders the spread as
 * `<div style={{ width: bleedWidth }}>`, i.e. those points straight into CSS px.
 *
 * Text must use the same scale or it is sized against a different ruler than the page it sits on.
 * A stored `pointSize` is therefore written to CSS as `px` and measured on canvas at `${pt}px` — no
 * 96/72 anywhere. That also matches yb360, which measures with
 * `context.font = fontSize + "px " + fontFamily` where `fontSize` is the point size
 * (`odeditorsvg.js:14048`), so larry's baked `TA`/`BL`/`TW` land where yb360 draws them.
 *
 * Going through 96/72 here is what made every glyph 4/3 too large relative to the page and wrapped
 * every line ~25% early.
 */

/** Canvas `measureText` gets unreliable at very small sizes, so measurement floors here and the
 *  caller scales the result back down by the real point size (see `measureFontAscentDescentPt`).
 *  7.5pt is the old 10px floor carried across the unit change unchanged. */
export const CANVAS_FONT_MIN_PT = 7.5

/** Builds a `ctx.font` string for the given run-level typography, resolving family/weight/style
 *  through `fonts` (falling back to a generic family/weight/style when unresolved). */
export function resolveCanvasFont(
  pointSize: number,
  fontFamilyId: number | undefined,
  fontId: number | undefined,
  fonts?: ParagraphBlockFontLookup,
  fallbackCssFamily = 'sans-serif'
): string {
  const px = Math.max(CANVAS_FONT_MIN_PT, pointSize)
  const cssFamily =
    fontFamilyId && fonts?.getFamilyCssName(fontFamilyId)
      ? `"${fonts.getFamilyCssName(fontFamilyId)}", sans-serif`
      : fallbackCssFamily
  const variant = fontId != null ? fonts?.getVariantByFontId(fontId) : undefined
  const weight = variant?.bold ? 700 : 400
  const style = variant?.italic ? 'italic' : 'normal'

  return `${style} ${weight} ${px}px ${cssFamily}`
}

/** tracking/kerning are both stored as 1/1000 em (print industry convention); converts to a
 *  CSS `letterSpacing` length for the given run's size in points (= CSS px, see the note above). */
export function letterSpacingCss(tracking: number, kerning: number, runPt: number): string {
  const spacing = letterSpacingPt(tracking, kerning, runPt)

  return spacing !== 0 ? `${spacing}px` : '0px'
}

/** The same tracking + kerning sum as `letterSpacingCss`, as a number of points, for callers that
 *  do arithmetic with it (per-glyph advance) rather than hand it to CSS. */
export function letterSpacingPt(tracking: number, kerning: number, runPt: number): number {
  return ((tracking + kerning) / 1000) * runPt
}

/** Ratio fallbacks for when canvas font metrics are unavailable (no DOM, e.g. jsdom tests).
 *  Native yb360 ascent/descent sit in the 0.70–0.833 / 0.15–0.167 × pointSize band. */
const FALLBACK_ASCENT_RATIO = 0.8
const FALLBACK_DESCENT_RATIO = 0.2

/**
 * Measures the run font's ascent (line top → baseline, yb360 BL) and descent (below-baseline
 * extent, yb360 DES) in points via canvas `fontBoundingBoxAscent/Descent` — the same font-wide
 * metrics CSS `line-height: normal` uses, which is how this editor renders text. Ratios are
 * derived against the actual size the font string used (resolveCanvasFont floors at
 * `CANVAS_FONT_MIN_PT`), so small point sizes are not inflated.
 */
export function measureFontAscentDescentPt(
  ctx: CanvasRenderingContext2D | null,
  pointSize: number,
  fontFamilyId: number | undefined,
  fontId: number | undefined,
  fonts?: ParagraphBlockFontLookup
): FontVerticalMetricsPt {
  const fallback: FontVerticalMetricsPt = {
    ascentPt: FALLBACK_ASCENT_RATIO * pointSize,
    descentPt: FALLBACK_DESCENT_RATIO * pointSize,
  }

  if (!ctx) return fallback

  ctx.font = resolveCanvasFont(pointSize, fontFamilyId, fontId, fonts)

  const metrics = ctx.measureText('Mg')
  const ascentPx = metrics.fontBoundingBoxAscent
  const descentPx = metrics.fontBoundingBoxDescent

  if (!Number.isFinite(ascentPx) || !Number.isFinite(descentPx)) return fallback

  const sizeUsed = Math.max(CANVAS_FONT_MIN_PT, pointSize)

  return {
    ascentPt: (ascentPx / sizeUsed) * pointSize,
    descentPt: (descentPx / sizeUsed) * pointSize,
  }
}

/** Cap-height-as-fraction-of-point-size fallback for when no canvas context is available (jsdom,
 *  SSR). Distinct from `FALLBACK_ASCENT_RATIO` above: that approximates a font's full ascent
 *  (room for accents/ascenders across arbitrary text), while this approximates a single capital
 *  letter's actual ink — typically shorter. 0.7 is a common typographic cap-height rule of thumb. */
const CAP_INK_HEIGHT_RATIO_FALLBACK = 0.7

/**
 * Measures a single glyph's actual rendered ink extent (`TextMetrics.actualBoundingBox*`) — the
 * tight bounds of what's actually drawn, not the font-wide box `measureFontAscentDescentPt` reads.
 * Needed to fit a drop cap to a target height precisely: different capital letters, and different
 * fonts, have different ink proportions relative to their em box (a script "Q" with a tail extends
 * further than a monoline sans "I"), so a font-wide metric would fit the wrong thing.
 */
export function measureGlyphInkMetricsPt(
  ctx: CanvasRenderingContext2D | null,
  character: string,
  pointSize: number,
  fontFamilyId: number | undefined,
  fontId: number | undefined,
  fonts?: ParagraphBlockFontLookup
): GlyphInkMetricsPt {
  const fallback: GlyphInkMetricsPt = {
    inkAscentPt: CAP_INK_HEIGHT_RATIO_FALLBACK * pointSize,
    inkDescentPt: 0,
  }

  if (!ctx || !character) return fallback

  ctx.font = resolveCanvasFont(pointSize, fontFamilyId, fontId, fonts)

  const metrics = ctx.measureText(character)
  const ascentPx = metrics.actualBoundingBoxAscent
  const descentPx = metrics.actualBoundingBoxDescent

  if (!Number.isFinite(ascentPx) || !Number.isFinite(descentPx)) return fallback

  const sizeUsed = Math.max(CANVAS_FONT_MIN_PT, pointSize)

  return {
    inkAscentPt: (ascentPx / sizeUsed) * pointSize,
    inkDescentPt: (descentPx / sizeUsed) * pointSize,
  }
}

export interface DropCapFontSizeFit {
  fontSizePt: number
  /** The solved size's own measured ink height in points — equal to `targetInkHeightPt` within
   *  `DROP_CAP_SOLVE_TOLERANCE_PT`, or the best reached before `DROP_CAP_SOLVE_MAX_ITERATIONS`. */
  achievedInkHeightPt: number
}

const DROP_CAP_SOLVE_TOLERANCE_PT = 0.05
const DROP_CAP_SOLVE_MAX_ITERATIONS = 4
/** Arbitrary seed point size for the first ink measurement. Any positive value works — the solve
 *  rescales from whatever ink height it measures there — 100 just sits comfortably above
 *  `CANVAS_FONT_MIN_PT` for good measurement precision. */
const DROP_CAP_SOLVE_SEED_PT = 100

/**
 * Solves the font size at which `character`'s actual rendered ink height equals
 * `targetInkHeightPt` — the drop cap sizing rule ("fitted to the glyph's actual rendered bounding
 * box... not a flat multiplier of a base size").
 *
 * Deliberately a fit against a real measurement, not a hardcoded ratio table: ink height scales
 * very close to linearly with point size for vector (outline) fonts, so a single measurement and
 * rescale converges in one further iteration for almost any font — but the exact ratio is
 * font‑and‑glyph‑specific (a script face's swashes are not a monoline sans's), which is exactly
 * why a fixed ratio was wrong. The loop exists to absorb whatever small non-linearity a real
 * browser's rasterizer introduces, not because the underlying relationship is expected to be
 * dramatically non-linear.
 */
export function solveDropCapFontSizePt(
  ctx: CanvasRenderingContext2D | null,
  character: string,
  targetInkHeightPt: number,
  fontFamilyId: number | undefined,
  fontId: number | undefined,
  fonts?: ParagraphBlockFontLookup
): DropCapFontSizeFit {
  if (!Number.isFinite(targetInkHeightPt) || targetInkHeightPt <= 0) {
    return { fontSizePt: 0, achievedInkHeightPt: 0 }
  }

  if (!ctx || !character) {
    const fontSizePt = targetInkHeightPt / CAP_INK_HEIGHT_RATIO_FALLBACK

    return { fontSizePt, achievedInkHeightPt: targetInkHeightPt }
  }

  let sizePt = DROP_CAP_SOLVE_SEED_PT

  for (let i = 0; i < DROP_CAP_SOLVE_MAX_ITERATIONS; i++) {
    const { inkAscentPt, inkDescentPt } = measureGlyphInkMetricsPt(
      ctx,
      character,
      sizePt,
      fontFamilyId,
      fontId,
      fonts
    )
    const inkHeightPt = inkAscentPt + inkDescentPt

    if (!Number.isFinite(inkHeightPt) || inkHeightPt <= 0) {
      // Degenerate glyph (e.g. whitespace draws no ink) — nothing to scale from.
      return {
        fontSizePt: targetInkHeightPt / CAP_INK_HEIGHT_RATIO_FALLBACK,
        achievedInkHeightPt: targetInkHeightPt,
      }
    }

    if (Math.abs(inkHeightPt - targetInkHeightPt) <= DROP_CAP_SOLVE_TOLERANCE_PT) {
      return { fontSizePt: sizePt, achievedInkHeightPt: inkHeightPt }
    }

    sizePt = sizePt * (targetInkHeightPt / inkHeightPt)
  }

  const final = measureGlyphInkMetricsPt(ctx, character, sizePt, fontFamilyId, fontId, fonts)

  return { fontSizePt: sizePt, achievedInkHeightPt: final.inkAscentPt + final.inkDescentPt }
}

/** Advance-width-as-fraction-of-point-size fallback for when no canvas context is available. A
 *  typical capital's set-width sits around 0.6em for many faces — a rough estimate, same spirit as
 *  this module's other no-canvas fallbacks. */
const CAP_ADVANCE_WIDTH_RATIO_FALLBACK = 0.6

/**
 * Advance width of a single glyph at the given size — `TextMetrics.width`, the standard layout
 * advance used for cursor/wrap positioning (not the tight ink bbox `measureGlyphInkMetricsPt`
 * reads, which exists purely to fit the cap's *height*). This is what a drop cap's own indent is
 * measured against: how much horizontal space the enlarged glyph needs set aside for it, the same
 * notion `measureTextPt` already applies to ordinary run text.
 */
export function measureGlyphAdvanceWidthPt(
  ctx: CanvasRenderingContext2D | null,
  character: string,
  pointSize: number,
  fontFamilyId: number | undefined,
  fontId: number | undefined,
  fonts?: ParagraphBlockFontLookup
): number {
  const fallback = CAP_ADVANCE_WIDTH_RATIO_FALLBACK * pointSize

  if (!ctx || !character) return fallback

  ctx.font = resolveCanvasFont(pointSize, fontFamilyId, fontId, fonts)

  const widthPx = ctx.measureText(character).width

  if (!Number.isFinite(widthPx)) return fallback

  const sizeUsed = Math.max(CANVAS_FONT_MIN_PT, pointSize)

  return (widthPx / sizeUsed) * pointSize
}
